import { freshDataUiState, type DataUiSliceState } from './data-ui-slice'
import { INITIAL_CHAT_CONTEXT } from './story-slice'
import type { ChatContext } from './chat-types'
import { INITIAL_CONVERSATION_STATE } from './conversation-types'
import type { ConversationState } from './conversation-types'
import { INITIAL_ACTIVE_RAIL_ITEM } from '@/shell/ide-v2/rail-defaults'
import { SHELL_CHAT_DRAWER_DEFAULT_OPEN } from '@/shell/shell-chat-drawer-defaults'
import type { RailItemId } from '@/shell/ide-v2/types'

/**
 * The project SESSION surface — where the user was inside the project they
 * are leaving — released by `closeProject` in the same transaction that
 * clears the project MODEL.
 *
 * ── What was measured ──────────────────────────────────────────────────────
 * slice-5A census §A5-a (`docs/superpowers/specs/2026-08-18-slice5-ide-
 * editors.md`, repair R2). Leaving project A through the account menu left
 * `currentProjectId`, `datasourcesByBlock` and `flowNames` correctly cleared
 * — and `chatContext`, `activeRailItem` and `selectedEntityId` pinned to the
 * closed project. Consequences, both read off the running app and not
 * inferred:
 *   · the dashboard rendered the data-scoped composer instead of the hero
 *     composer (`[data-hero-chat]` and `overview-chat-start` both invisible);
 *   · a "make me an app" prompt submitted from there NEVER REACHED `/ide/`
 *     (`A5b-project-b-arrival.reachedSecond: false`,
 *     `secondSubmitError: null` — a silent stall, no error raised);
 *   · the right rail displayed project A's variable id
 *     (`document-1-overview.variables.opened`) on the dashboard with no
 *     project open.
 * A never-opened session read the inverse on the identical readers
 * (`A5b-fresh-dashboard-control`), which is what makes the leaked state the
 * cause rather than the readers.
 *
 * ── This is NOT sign-out ───────────────────────────────────────────────────
 * `auth-slice.ts`'s `clearSignOutContentKeys` is the neighbouring hygiene
 * pass and a DIFFERENT scope: it clears BROWSER STORAGE that would leak one
 * identity's content to the next identity on a shared machine. This clears
 * IN-MEMORY POSITION that belongs to one project's session. Nothing here
 * touches storage, and nothing there touches these fields; folding either
 * into the other would give one key two owners.
 *
 * ── Device and preference state is deliberately untouched ──────────────────
 * Theme, onboarding status, persona, locale, panel widths, rail expansion,
 * devtools/bottom-bar state: none of it describes a project, so none of it
 * dies with one. That boundary is the discriminator the regression lock
 * asserts (`store/__tests__/project-exit-session-reset.test.ts`), not a
 * comment: a reset that also took preferences with it would pass a
 * "everything is back to default" check and still be wrong.
 *
 * ── Derived vs enumerated (asked for explicitly, answered honestly) ────────
 * DERIVED — `INITIAL_DATA_UI_STATE`. The whole `DataUiSlice` is one project's
 *   data-navigator position, so the exit takes the WHOLE object instead of
 *   naming `selectedEntityId` and `dataCategory` by hand. A field added to
 *   that slice is released here without anyone remembering to come back.
 *
 * ENUMERATED, with the reason derivation fails for each: the remaining keys
 *   are written by IN-PROJECT INTERACTION, not by any project-open path, so
 *   there is no producer to derive them from. Reading them off the open
 *   transaction — the obvious mechanical rule — yields the empty set for
 *   exactly the two keys the census named as the cause. Each therefore gets
 *   a line and a reason:
 *
 *   · `chatContext` — its populated form is a set of coordinates INSIDE one
 *     project (authority domain, authority view, surface id, selected
 *     property key). `getStudioComposerPlaceholder`
 *     (features/chat/services/studio-task-orchestrator.ts:385) branches on
 *     `authorityDomain && authorityView` ahead of everything else, which is
 *     how the dashboard composer came to describe a closed project's Data
 *     surface. Written by surface activation, never by an open path.
 *
 *   · `activeRailItem` — names an IDE surface, and no IDE surface exists
 *     with no project open. PARTLY derivable (`importCogoApplication` does
 *     write it) but not from the path the walk took: `openServerProject`
 *     leaves it alone, so a strict open-path derivation would have missed it.
 *
 *   · `chatSidebarOpen` — this is the key that actually hid the hero
 *     composer, and it is the one an open→close identity check cannot see:
 *     EVERY open path sets it `true`, but the slice's own initial value is
 *     ALSO `true` (`ui-slice.ts:136`), so open→close is already an identity
 *     on it and a diff-based gate reports nothing. Its correct exit value is
 *     not the slice initial but the DASHBOARD SHELL's declared default —
 *     leaving a project lands on the dashboard — so it is read from
 *     `SHELL_CHAT_DRAWER_DEFAULT_OPEN`, the same table the shells apply.
 *     Why the shells do not already handle it: `useChatDefaultForShell` has
 *     exactly one caller (`'dashboard'`), nothing ever applies `'ide'`, so
 *     its `lastAppliedShell` guard early-returns on every dashboard mount
 *     after the first — see the note in `shell/shell-chat-defaults.ts`.
 *
 *   · `conversation` — slice 6 §2.3a (2026-08-18): after leaving project A
 *     the store still held EIGHT `.conversation` paths of A's confirmed
 *     narrative, `conversation.phase` read `"generating"` with no project
 *     open, and the dashboard hero composer — the control that creates the
 *     NEXT project — read `s.conversation.designSystemPreset` off the
 *     closed one (`hero-chat-input.tsx`). B5 proved the crossing unbounded
 *     on the OPEN path: open A after leaving B and B's conversation is A's
 *     live conversation, indefinitely. The slice DECLARED `resetConversation`
 *     and only tests called it — the project's dominant defect class in
 *     canonical shape. Its body is one assignment of
 *     `INITIAL_CONVERSATION_STATE`; applied here, in the same transaction,
 *     from the same constant, so the two cannot drift. Written by the chat
 *     pipeline during in-project work, never by an open path — which is why
 *     open-path derivation missed it.
 *
 * ── What is deliberately left alone, so omission is a decision ─────────────
 *   · `chatMessages` / the conversation transcript — this codebase has
 *     already decided (twice, in `clearExecutableChatConfirmationsFor
 *     ProjectReplacement` and in three tests named "…but keeps conversation
 *     history") that ordinary transcript survives; and on the CLOSE path
 *     `clearDispatch()` already empties it. Not this function's field.
 *   · `studioMode`, `viewport`, `designSystem` — written by the open path
 *     and not restored, but every one of them is overwritten by the next
 *     open and none was measured being read on the dashboard. They are
 *     recorded in the allowlist of the open→close identity gate rather than
 *     cleared on a hunch.
 */
export interface ProjectSessionSurfaceState extends DataUiSliceState {
  chatContext: ChatContext
  activeRailItem: RailItemId
  chatSidebarOpen: boolean
  conversation: ConversationState
}

export function resetProjectSessionSurfaceOnExit(
  state: ProjectSessionSurfaceState,
): void {
  Object.assign(state, freshDataUiState())
  state.chatContext = { ...INITIAL_CHAT_CONTEXT }
  state.activeRailItem = INITIAL_ACTIVE_RAIL_ITEM
  state.chatSidebarOpen = SHELL_CHAT_DRAWER_DEFAULT_OPEN.dashboard
  state.conversation = { ...INITIAL_CONVERSATION_STATE }
}
